import { colors, screen } from 'components/styles/variables';
import Navbar from 'components/Navbar';
import { ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import useHashLocation from 'lib/hooks/useHashLocation';

// assets
import autolistLogoRed from 'public/static/images/autolist-logo-red.png';
import arrowLeft from 'public/static/images/arrow-left.png';
import { inIframe } from 'lib/Utilities';
import DirectToVdpContext from 'contexts/DirectToVdpContext';
import Link from 'next/link';
import { useLog } from './Navbar/Desktop/useNavBarClickLog';
import LeaderboardAds from './Ad/LeaderboardAds';

export interface HeaderProps {
  url?: string;
  subNav?: ReactNode;
  includeAds?: boolean;
  isVdp?: boolean;
  showOnScroll?: boolean;
  vdpUrl?: string;
}

export function getHighlightedNavbarItem(url = ''): string | undefined {
  let highlightedNavbarItem: string | undefined;

  if (url.includes('news-and-analysis')) {
    highlightedNavbarItem = 'newsAndAnalysis';
  }

  if (url === '/') {
    highlightedNavbarItem = 'home';
  }

  if (url.includes('guides')) {
    highlightedNavbarItem = 'guidesAndReviews';
  }

  if (url.includes('listings')) {
    highlightedNavbarItem = 'search';
  }

  if (url.includes('favorites')) {
    highlightedNavbarItem = 'favorites';
  }

  return highlightedNavbarItem;
}

function removeVin(str: string | undefined): string | undefined {
  if (str === undefined) return str;

  return str
    .split('&')
    .filter((param) => !param.startsWith('vin='))
    .join('&');
}

function Header({
  url,
  subNav,
  includeAds = true,
  isVdp = false,
  showOnScroll = false,
  vdpUrl,
}: HeaderProps): JSX.Element {
  const router = useRouter();
  let { back } = router;
  const hashLocation = useHashLocation();
  const { isDirectToVdp } = useContext(DirectToVdpContext);

  if (inIframe()) {
    back = (): void => {
      // SRP is wrapping VDP in an iframe. To go back, message the SRP with a 'srp' message.
      window.parent.postMessage('srp', '*');
    };
  } else {
    back = (): void => {
      const urlBase = router.asPath.split('#')[0];
      if (isDirectToVdp && vdpUrl) {
        window.location.assign(`${window.location.origin}${vdpUrl.split('#')[0]}`);
      } else if (urlBase && urlBase !== '/listings' && !urlBase.includes('vin')) {
        router.push(urlBase);
      }
      // if there is a vin query param, we sent the user to a VDP from a native app link
      // when user navigates back, we want to remove the query AND hash vin params from the URL
      else if (urlBase && router.query.vin) {
        const { asPath } = router;

        let path;
        let queryString;
        let hashParams;

        [path, queryString] = asPath.split('?');

        if (queryString) {
          [queryString, hashParams] = queryString.split('#');
          queryString = removeVin(queryString);
        } else if (path) {
          [path, hashParams] = path.split('#');
        }

        hashParams = removeVin(hashParams);

        const newPath = `${path}${queryString ? `?${queryString}` : ''}${
          hashParams ? `#${hashParams}` : ''
        }`;
        router.push(newPath);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { vin, ...hashLocationValues } = hashLocation.values;
        hashLocation.navigate(hashLocationValues);
      }
    };
  }

  return (
    <header data-testid="header-element">
      {includeAds && <LeaderboardAds />}
      <div className="al-container">
        {isVdp ? <BackToSearch onClick={back} showEnhancement={isDirectToVdp} /> : <AutolistLogo />}
        <Navbar currentItem={getHighlightedNavbarItem(url)} />
      </div>
      {subNav}
      <style jsx>{`
        header {
          position: relative;
          z-index: 600;
          border-bottom: 1px solid ${colors.G1};
          background-color: white;
        }

        .al-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          height: 63px;
          margin-right: auto;
          margin-left: auto;
          padding-left: 15px;
          padding-right: 15px;
        }

        @media (min-width: ${screen.smMin}) {
          .al-container {
            width: 750px;
          }
        }

        @media (min-width: ${screen.mdMin}) {
          .al-container {
            width: 970px;
          }
        }

        @media (min-width: ${screen.lgMin}) {
          .al-container {
            width: 100%;
            padding-left: 24px;
            padding-right: 24px;
          }
        }

        @media (max-width: ${screen.smMin}) {
          header {
            position: ${showOnScroll ? 'fixed' : 'relative'};
            width: 100%;
          }
        }
      `}</style>
    </header>
  );
}

interface BackToSearchProps {
  onClick: () => void;
  showEnhancement?: boolean;
}

function BackToSearch({ onClick, showEnhancement }: BackToSearchProps): JSX.Element {
  const { t } = useTranslation();
  const log = useLog();

  return (
    <button
      className="back-button-container"
      role="button"
      onClick={(): void => {
        log('back_to_search_tap');
        onClick();
      }}
    >
      <div data-testid="back-icon" className="back-icon" />
      <a className="back-button" suppressHydrationWarning={true}>
        {showEnhancement ? t('vdp.viewMoreCars') : t('vdp.backToSearch')}
      </a>
      <style jsx>{`
        .back-button-container {
          display: flex;
          flex-direction: row;
          cursor: pointer;
          background: none;
          border: none;
          padding: 0;
          color: inherit;
          font: inherit;
          outline: inherit;
        }

        .back-icon {
          width: 15px;
          height: 21px;
          background-image: url('${arrowLeft}');
          background-size: 12px 21px;
          background-repeat: no-repeat;
          margin-right: 2px;
        }
      `}</style>
    </button>
  );
}

function AutolistLogo(): JSX.Element {
  const log = useLog();

  return (
    <>
      <Link href="/" legacyBehavior prefetch={false}>
        <a
          data-testid="logo"
          onClick={(): void => {
            log('autolist_icon_tap');
          }}
          className="logo"
        />
      </Link>
      <style jsx>{`
        .logo {
          display: inline-block;
          width: 137px;
          height: 20px;
          background-image: url('${autolistLogoRed}');
          background-size: 137px 20px;
          background-repeat: no-repeat;
        }
      `}</style>
    </>
  );
}

export default Header;
